/* eslint-disable no-console */
import { useOperationDespatchContext } from "../../../../../../../contexts/DespatchContext";
import { DespatchCrew } from "Models/despatch.crew";
import { DespatchTask } from "Models/despatch.task";
import _ from "lodash";

import {
  analyzerSlotSourceId,
  DNDFieldsNames,
  getCurrentSlotConfig,
  DNDSlotsConfig,
  DNDSourceTypes,
  DNDSlotTypes,
} from "./slotTypes";

interface UpdateFieldParams {
  slotElement: any;
  sourceElement: any;
  destinyFieldName: string;
  defaultValue: any;
  originFieldName?: string;
  onBeforeUpdateFieldGuard?: DNDSlotsConfig["onBeforeUpdateFieldGuard"];
}

// Pragmatic Drag and Drop event types
interface DragDropSource {
  data: any;
  element: Element;
}

interface DragDropLocation {
  current: {
    dropTargets: Array<{
      data: any;
      element: Element;
    }>;
  };
  initial: {
    dropTargets: Array<{
      data: any;
      element: Element;
    }>;
  };
}

interface PragmaticDropEvent {
  source: DragDropSource;
  location: DragDropLocation;
}

interface PragmaticDragStartEvent {
  source: DragDropSource;
}

export const useDragSystem = () => {
  const {
    members,
    equipments,
    storageBoxes,
    teams,
    crews,
    tasks,
    updateCrewById,
    updateTaskById,
    moveTaskBetweenCrews,
    setCurrentDraggable,
    selectedOption,
    setParentOfSource,
    parentOfSource,
  } = useOperationDespatchContext();

  const sourceDataDictionary = {
    [DNDFieldsNames.LIST_MEMBERS]: members,
    [DNDFieldsNames.LIST_FLEETS]: equipments,
    [DNDFieldsNames.LIST_STORAGE_BOXES]: storageBoxes,
    [DNDFieldsNames.LIST_TEAMS]: teams,
    [DNDFieldsNames.LIST_ORDERS]: tasks,
    [DNDFieldsNames.LIST_CREWS]: crews,
    [DNDFieldsNames.CARD_TEAM]: crews,
  };

  const getSourceByFieldName = (name: DNDFieldsNames) => {
    return sourceDataDictionary[name as keyof typeof sourceDataDictionary];
  };

  const getSourceElementByIdAndFieldName = (id: string | number, name: DNDFieldsNames) => {
    const source = getSourceByFieldName(name);

    if (!source) return;
    // @ts-ignore
    const element = source.find((el: any) => el.id === id);

    return element;
  };

  const slotDataDictionary = {
    [DNDFieldsNames.CARD_TEAM]: crews,
    [DNDFieldsNames.CARD_ORDER]: tasks,
  };

  const getSlotDataByFieldName = (name: DNDFieldsNames) => {
    return slotDataDictionary[name as keyof typeof slotDataDictionary];
  };

  const getSlotDataElementByIdAndFieldName = (id: string | number, name: DNDFieldsNames) => {
    const slot = getSlotDataByFieldName(name);

    if (!slot) return;
    // @ts-ignore
    const element = slot.find((el: any) => el.id === id);

    return element;
  };

  const updateHandler = (
    fieldName: DNDFieldsNames,
    slotElement?: DespatchCrew | DespatchTask,
    whatHasChanged?: string[]
  ) => {
    console.log("🔄 updateHandler called:", {
      fieldName,
      hasSlotElement: !!slotElement,
      whatHasChanged,
    });

    if (!slotElement) {
      console.log("❌ No slot element, returning early");
      return;
    }

    try {
      if (fieldName === DNDFieldsNames.CARD_TEAM) {
        console.log("🔄 Processing CARD_TEAM update");
        const crew = _.cloneDeep(slotElement as DespatchCrew);
        console.log("🔄 Crew cloned, calling updateCrewById");
        updateCrewById(crew.id, crew, false, undefined, whatHasChanged);
        console.log("🔄 updateCrewById completed");
        return;
      }

      if (fieldName === DNDFieldsNames.CARD_ORDER) {
        console.log("🔄 Processing CARD_ORDER update");
        updateTaskById(slotElement.id, slotElement as DespatchTask, whatHasChanged);
        console.log("🔄 updateTaskById completed");
      }
    } catch (error) {
      console.error("💥 Error in updateHandler:", error);
      throw error;
    }
  };

  const updateOldFieldHandler = (
    fieldName: DNDFieldsNames,
    slotElement?: DespatchCrew | DespatchTask,
    sourceElementId?: number,
    parentElement?: Partial<DespatchCrew>
  ) => {
    if (!slotElement) return;

    if (fieldName === DNDFieldsNames.LIST_ORDERS) {
      if (parentElement && sourceElementId) {
        let currentTeam = parentElement;
        const currentTasks = currentTeam?.tasks?.filter((task) => task.id !== sourceElementId);
        currentTeam = { ...(currentTeam || {}), tasks: currentTasks };

        updateCrewById(
          slotElement.id,
          slotElement as DespatchCrew,
          false,
          currentTeam as DespatchCrew
        );

        return;
      }

      const crew = _.cloneDeep(slotElement as DespatchCrew);
      updateCrewById(crew.id, crew);
    }

    return;
  };

  // NEW: Pragmatic drag start handler
  const handleDragStart = ({ source }: PragmaticDragStartEvent) => {
    console.log("🚀 handleDragStart:", source.data);

    // Extract the sourceId from the data passed in getInitialData
    const sourceId = source.data.sourceId;

    if (sourceId) {
      setCurrentDraggable(analyzerSlotSourceId(sourceId));
    }

    // If there's a parent slot ID in the data, track it
    if (source.data.parentSlotId) {
      setParentOfSource(analyzerSlotSourceId(source.data.parentSlotId));
    }
  };

  const removeLocalElementByField = (
    slotElement: any,
    fieldName: string,
    defaultValue: any,
    id?: number
  ) => {
    console.log("🔧 removeLocalElementByField:", { fieldName, id });

    // Create a safe copy
    const result = { ...slotElement };

    // Handle nested team fields
    if (fieldName?.includes("team.")) {
      const nestedField = fieldName.split("team.")[1];

      if (!result.team) {
        console.warn("No team property found on slotElement");

        return result;
      }

      // Ensure team is also copied
      result.team = { ...result.team };

      switch (nestedField) {
        case "members":
          if (Array.isArray(result.team.members) && id) {
            result.team.members = result.team.members.filter((member: any) => member.id !== id);
          } else {
            result.team.members = defaultValue;
          }
          break;

        case "fleets":
          if (Array.isArray(result.team.fleets) && id) {
            result.team.fleets = result.team.fleets.filter((fleet: any) => fleet.id !== id);
          } else {
            result.team.fleets = defaultValue;
          }
          break;

        case "memberLeader":
          result.team.memberLeader = defaultValue;
          break;

        default:
          console.warn("Unknown nested team field:", nestedField);
      }

      return result;
    }

    // Handle specific known field names directly
    switch (fieldName) {
      case "tasks":
        if (Array.isArray(result.tasks) && id) {
          result.tasks = result.tasks.filter((task: any) => task.id !== id);
        } else {
          result.tasks = defaultValue;
        }
        break;

      case "members":
        if (Array.isArray(result.members) && id) {
          result.members = result.members.filter((member: any) => member.id !== id);
        } else {
          result.members = defaultValue;
        }
        break;

      case "fleets":
        if (Array.isArray(result.fleets) && id) {
          result.fleets = result.fleets.filter((fleet: any) => fleet.id !== id);
        } else {
          result.fleets = defaultValue;
        }
        break;

      case "storageBoxes":
        if (Array.isArray(result.storageBoxes) && id) {
          result.storageBoxes = result.storageBoxes.filter((box: any) => box.id !== id);
        } else {
          result.storageBoxes = defaultValue;
        }
        break;

      case "memberLeader":
        result.memberLeader = defaultValue;
        break;

      default:
        console.warn("Unknown field name:", fieldName);
        // Fallback to original behavior but with safety
        if (result[fieldName]) {
          if (Array.isArray(result[fieldName]) && id) {
            result[fieldName] = result[fieldName].filter((item: any) => item.id !== id);
          } else {
            result[fieldName] = defaultValue;
          }
        }
    }

    return result;
  };

  const removeElementFromDespatch = (removedId: string, id?: number) => {
    console.log("🗑️ Starting removal:", { removedId, id, timestamp: Date.now() });

    try {
      const removedPayload = analyzerSlotSourceId(removedId);

      if (!removedPayload.isValid || !removedPayload.name || removedPayload.type !== "slot") {
        console.log("❌ Invalid payload, returning early");
        return;
      }

      const slotConfig = getCurrentSlotConfig(selectedOption, removedPayload.name);

      if (!slotConfig) {
        console.log("❌ No slot config found, returning early");
        return;
      }

      const changedFields = slotConfig?.fields.map(
        (field) =>
          field.originName ||
          (field.destinyName?.includes("team")
            ? field.destinyName?.split("team.")[1]
            : field.destinyName)
      );

      let slotElement = getSlotDataElementByIdAndFieldName(
        removedPayload.id!,
        removedPayload.fieldName
      );

      if (!slotElement) {
        console.log("❌ No slot element found, returning early");
        return;
      }

      slotElement = slotConfig.fields.reduce((slotElementUpdated, field, index) => {
        const result = removeLocalElementByField(
          slotElementUpdated,
          field.destinyName,
          field.defaultValue,
          id
        );

        return result;
      }, slotElement);

      updateHandler(removedPayload.fieldName, slotElement, changedFields);
    } catch (error: any) {
      console.error("💥 Error in removeElementFromDespatch:", error);
      console.error("💥 Stack trace:", error?.stack);
    }
  };

  const updateLocalElementByFieldName = (params: UpdateFieldParams) => {
    console.log("🔧 updateLocalElementByFieldName started:", params.destinyFieldName);

    try {
      const {
        slotElement,
        defaultValue,
        destinyFieldName,
        originFieldName,
        onBeforeUpdateFieldGuard,
      } = params;

      let { sourceElement } = params;

      // Safe property access instead of dot.get
      if (originFieldName) {
        console.log("🔧 Getting origin field:", originFieldName);
        if (originFieldName.includes(".")) {
          const fieldParts = originFieldName.split(".");
          sourceElement = fieldParts.reduce((obj, part) => {
            return obj && obj[part] !== undefined ? obj[part] : undefined;
          }, sourceElement);
        } else {
          sourceElement = sourceElement[originFieldName];
        }
      }

      const payload = {
        sourceElement,
        slotElement,
        destinyFieldName,
        originFieldName,
      };

      console.log("🔧 Calling before update field guard");
      const result = onBeforeUpdateFieldGuard?.(payload);

      if (result === false) {
        console.log("🔧 Before update field guard returned false");
        return payload.slotElement;
      }

      console.log("🔧 Creating safe copy without circular references");

      // AVOID JSON.parse(JSON.stringify()) - it breaks on circular references
      // Instead, use shallow copy and directly manipulate the field we need
      const updatedSlotElement = { ...payload.slotElement };

      // Get current field value safely
      let slotElementFieldValue;
      if (destinyFieldName.includes(".")) {
        const fieldParts = destinyFieldName.split(".");
        slotElementFieldValue = fieldParts.reduce((obj, part) => {
          return obj && obj[part] !== undefined ? obj[part] : undefined;
        }, updatedSlotElement);
      } else {
        slotElementFieldValue = updatedSlotElement[destinyFieldName];
      }

      console.log(
        "🔧 Current field value type:",
        typeof slotElementFieldValue,
        Array.isArray(slotElementFieldValue)
      );

      // If field doesn't exist, initialize it
      if (slotElementFieldValue === undefined) {
        console.log("🔧 Field doesn't exist, initializing with default value");
        slotElementFieldValue = defaultValue;
      }

      console.log("🔧 Processing field value update");

      let newFieldValue;
      if (Array.isArray(slotElementFieldValue)) {
        console.log("🔧 Processing array field, current length:", slotElementFieldValue.length);

        // Create new array with existing items plus new ones
        const sourceArray = Array.isArray(sourceElement) ? sourceElement : [sourceElement];
        const combined = [...slotElementFieldValue];

        // Add new items, avoiding duplicates by id
        for (const newItem of sourceArray) {
          if (newItem && !combined.find((existing) => existing.id === newItem.id)) {
            combined.push(newItem);
          }
        }

        newFieldValue = combined;
        console.log("🔧 New array length:", newFieldValue.length);
      } else {
        console.log("🔧 Processing non-array field");
        newFieldValue = sourceElement;
      }

      // Safe field setting without circular reference issues
      console.log("🔧 Setting new field value");
      if (destinyFieldName.includes(".")) {
        const fieldParts = destinyFieldName.split(".");
        let current = updatedSlotElement;

        // Navigate to parent and ensure objects exist
        for (let i = 0; i < fieldParts.length - 1; i++) {
          if (!current[fieldParts[i]]) {
            current[fieldParts[i]] = {};
          } else if (typeof current[fieldParts[i]] === "object") {
            current[fieldParts[i]] = { ...current[fieldParts[i]] };
          }
          current = current[fieldParts[i]];
        }

        // Set the final property
        current[fieldParts[fieldParts.length - 1]] = newFieldValue;
      } else {
        updatedSlotElement[destinyFieldName] = newFieldValue;
      }

      console.log("🔧 updateLocalElementByFieldName completed successfully");
      return updatedSlotElement;
    } catch (error: any) {
      console.error("💥 Error in updateLocalElementByFieldName:", error);
      console.error("💥 Error stack:", error.stack);
      // Return original element to prevent complete failure
      return params.slotElement;
    }
  };

  const handleDrop = ({ source, location }: PragmaticDropEvent) => {
    console.log("🚀 handleDrop started");

    try {
      setCurrentDraggable(null);

      const destination = location.current.dropTargets[0];

      if (!destination) {
        console.log("❌ No destination, returning early");
        return;
      }

      // Extract data from Pragmatic's source and destination
      const sourceData = source.data;
      const destinationData = destination.data;

      console.log("📦 Source data:", sourceData);
      console.log("📦 Destination data:", destinationData);

      // Reconstruct the draggableId and droppableId for the existing logic
      const draggableId = sourceData.sourceId; // This comes from getInitialData in draggable
      const droppableId = destinationData.slotId; // This comes from getData in dropTarget

      if (!draggableId || !droppableId) {
        console.log("❌ Missing draggableId or droppableId");
        return;
      }

      const draggablePayload = analyzerSlotSourceId(draggableId);
      const droppablePayload = analyzerSlotSourceId(droppableId);

      if (
        !draggablePayload.isValid ||
        !droppablePayload.isValid ||
        droppablePayload.type !== "slot" ||
        draggablePayload.type !== "source"
      ) {
        console.log("❌ Invalid payloads, returning early");
        return;
      }

      const slotConfig = getCurrentSlotConfig(selectedOption, droppablePayload.name);

      if (!slotConfig || !slotConfig.validSources.includes(draggablePayload.name)) {
        console.log("❌ Invalid slot config or source, returning early");
        return;
      }

      const changedFields = slotConfig?.fields.map(
        (field) =>
          field.originName ||
          (field.destinyName?.includes("team")
            ? field.destinyName?.split("team.")[1]
            : field.destinyName)
      );

      const sourceElement = getSourceElementByIdAndFieldName(
        draggablePayload.id,
        draggablePayload.fieldName
      );

      if (!sourceElement) {
        console.log("❌ No source element found, returning early");
        return;
      }

      let slotElement = getSlotDataElementByIdAndFieldName(
        droppablePayload.id,
        droppablePayload.fieldName
      );

      if (!slotElement) {
        console.log("❌ No slot element found, returning early");
        return;
      }

      //handling moving a task between teams
      if (
        droppablePayload.name === DNDSlotTypes.CREW_ORDERS &&
        draggablePayload.name === DNDSourceTypes.CREW_ORDER
      ) {
        const destinationTeamId = Number(droppablePayload.id);
        const taskId = Number(draggablePayload.id);
        const sourceTeamId = sourceData.parentTeamId;

        const task = tasks.find((t) => t.id === taskId);

        if (!task) {
          setParentOfSource(null);
          return;
        }

        moveTaskBetweenCrews(
          taskId,
          sourceTeamId ? Number(sourceTeamId) : -1,
          destinationTeamId,
          task
        );

        setParentOfSource(null);
        return;
      }

      const result = slotConfig.onBeforeUpdateGuard?.({
        sourceElement,
        slotElement,
        draggableField: draggablePayload.fieldName,
        droppableField: droppablePayload.fieldName,
      });

      if (result === false) {
        console.log("❌ Before update guard failed, returning early");
        return;
      }

      slotElement = slotConfig.fields.reduce((slotElementUpdated, field, index) => {
        const result = updateLocalElementByFieldName({
          slotElement: slotElementUpdated,
          sourceElement,
          destinyFieldName: field.destinyName,
          originFieldName: field.originName,
          defaultValue: field.defaultValue,
          onBeforeUpdateFieldGuard: slotConfig.onBeforeUpdateFieldGuard,
        });

        return result;
      }, slotElement);

      if (slotConfig.shouldUpdateSourceField) {
        const parentElement = getSlotDataElementByIdAndFieldName(
          parentOfSource?.id!,
          parentOfSource?.fieldName!
        );
        updateOldFieldHandler(
          draggablePayload.fieldName,
          slotElement,
          sourceElement.id,
          parentElement as DespatchCrew
        );
      } else {
        updateHandler(droppablePayload.fieldName, slotElement, changedFields);
      }

      setParentOfSource(null);

      console.log("✅ handleDrop completed successfully");
    } catch (error: any) {
      console.error("💥 Error in handleDrop:", error);
      console.error("💥 Stack trace:", error.stack);
    }
  };

  return {
    removeElementFromDespatch,
    handleDragStart,
    handleDrop,
    onDragStart: handleDragStart,
    onDragEnd: handleDrop,
  };
};
